/* eslint-disable kf/const-naming-convention */
import { isEqual } from "lodash-es";

import { formatDateTime } from "base/utils";

import {
  getCurrencyValue,
  resolveOrExecuteRelativeValue,
  getSelectedItemsInCheckList,
  isSameCurrencyUnit,
  VALID_DATE_RELATIVE_VALUES
} from "./utils.js";

const Text = {
  EQUAL_TO(LHS, RHS, data) {
    return LHS?.toLowerCase() === RHS?.toLowerCase();
  },
  NOT_EQUAL_TO(LHS, RHS, data) {
    return LHS?.toLowerCase() !== RHS?.toLowerCase();
  },
  CONTAINS(LHS, RHS, data) {
    return LHS?.toLowerCase().indexOf(RHS?.toLowerCase()) > -1;
  },
  NOT_CONTAINS(LHS, RHS, data) {
    return LHS?.toLowerCase().indexOf(RHS?.toLowerCase()) === -1;
  },
  MIN_LENGTH(LHS, RHS, data) {
    if (!RHS) return true;
    return LHS?.length >= RHS;
  },
  MAX_LENGTH(LHS, RHS, data) {
    if (!RHS) return true;
    return LHS?.length <= RHS;
  },
  PART_OF(LHS, RHS, data) {
    if (!RHS) return false;
    return LHS && RHS.includes(LHS);
  },
  NOT_PART_OF(LHS, RHS, data) {
    if (!RHS) return true;
    return LHS && !RHS.includes(LHS);
  },
  EMPTY(LHS, RHS, data) {
    return !LHS;
  },
  NOT_EMPTY(LHS, RHS, data) {
    return !!LHS;
  }
};

const BooleanClass = {
  EQUAL_TO(LHS, RHS, data) {
    return Boolean(LHS).toString() === RHS;
  },
  NOT_EQUAL_TO(LHS, RHS, data) {
    return Boolean(LHS).toString() !== RHS;
  }
};

const NumberClass = {
  EQUAL_TO(LHS, RHS, data) {
    return LHS === RHS;
  },
  NOT_EQUAL_TO(LHS, RHS, data) {
    return LHS !== RHS;
  },
  GREATER_THAN(LHS, RHS, data) {
    if (!RHS) return true;
    return LHS && LHS > RHS;
  },
  GREATER_THAN_OR_EQUAL_TO(LHS, RHS, data) {
    if (!RHS) return true;
    return LHS && LHS >= RHS;
  },
  LESS_THAN(LHS, RHS, data) {
    if (!RHS) return true;
    return LHS && LHS < RHS;
  },
  LESS_THAN_OR_EQUAL_TO(LHS, RHS, data) {
    if (!RHS) return true;
    return LHS && LHS <= RHS;
  },
  EMPTY(LHS, RHS, data) {
    return !LHS && LHS !== 0;
  },
  NOT_EMPTY(LHS, RHS, data) {
    return !!LHS || LHS === 0;
  }
};

const Attachment = {
  EMPTY(LHS, RHS, data) {
    return !LHS;
  },
  NOT_EMPTY(LHS, RHS, data) {
    return !!LHS;
  }
};

const User = {
  EQUAL_TO(LHS, RHS, data) {
    return LHS?._id === RHS?._id;
  },
  NOT_EQUAL_TO(LHS, RHS, data) {
    return LHS?._id !== RHS?._id;
  },
  IN(LHS, RHS, data) {
    if (!RHS && !LHS) return true;
    if (!RHS) return false;
    for (let user of RHS) {
      if (user._id === LHS?._id) return true;
    }
    return false;
  },
  EMPTY(LHS, RHS, data) {
    return !LHS;
  },
  NOT_EMPTY(LHS, RHS, data) {
    return !!LHS;
  }
};

const MultiUser = {
  CONTAINS(LHS, RHS, data) {
    if (!LHS && !RHS) return true;
    if (!LHS || !RHS) return false;
    let ns = new Set();
    LHS.forEach((user) => ns.add(user._id));
    RHS.forEach((user) => ns.add(user._id));
    return ns.size < LHS.length + RHS.length;
  },
  NOT_CONTAINS(LHS, RHS, data) {
    if (!LHS) return true;
    if (!RHS) return false;
    let ns = new Set();
    LHS.forEach((user) => ns.add(user._id));
    RHS.forEach((user) => ns.add(user._id));
    return ns.size === LHS.length + RHS.length;
  },
  EMPTY(LHS, RHS, data) {
    return LHS ? LHS.length === 0 : !LHS;
  },
  NOT_EMPTY(LHS, RHS, data) {
    return LHS ? LHS.length !== 0 : !!LHS;
  }
};

const Select = {
  EQUAL_TO(LHS, RHS, data) {
    return LHS === RHS;
  },
  NOT_EQUAL_TO(LHS, RHS, data) {
    return LHS !== RHS;
  },
  IN(LHS, RHS, data) {
    if (!RHS && !LHS) return true;
    for (let item of RHS) {
      if (item === LHS) return true;
    }
    return false;
  },
  EMPTY(LHS, RHS, data) {
    return !LHS;
  },
  NOT_EMPTY(LHS, RHS, data) {
    return !!LHS;
  }
};

const Multiselect = {
  CONTAINS(LHS, RHS, data) {
    if (!LHS && !RHS) return true;
    if (!LHS || !RHS) return false;
    let ns = new Set();
    LHS.forEach((item) => ns.add(item));
    RHS.forEach((item) => ns.add(item));
    return ns.size < LHS.length + RHS.length;
  },
  NOT_CONTAINS(LHS, RHS, data) {
    if (!LHS) return true;
    if (!RHS) return false;
    let ns = new Set();
    LHS.forEach((item) => ns.add(item));
    RHS.forEach((item) => ns.add(item));
    return ns.size === LHS.length + RHS.length;
  },
  EMPTY(LHS, RHS, data) {
    return LHS ? LHS.length === 0 : !LHS;
  },
  NOT_EMPTY(LHS, RHS, data) {
    return LHS ? LHS.length !== 0 : !!LHS;
  }
};

const Checklist = {
  CONTAINS(LHS, RHS, data) {
    let values = [];
    if (LHS) {
      for (let item of LHS) {
        if (item.selected) values.push(item.value);
      }
    }
    return values.includes(RHS);
  },
  NOT_CONTAINS(LHS, RHS, data) {
    let values = [];
    if (LHS) {
      for (let item of LHS) {
        if (item.selected) values.push(item.value);
      }
    }
    return !values.includes(RHS);
  },
  COUNT_EQUAL_TO(LHS, RHS, data) {
    return (
      (LHS && getSelectedItemsInCheckList(LHS)) === (RHS && parseInt(RHS, 10))
    );
  },
  COUNT_NOT_EQUAL_TO(LHS, RHS, data) {
    return (
      (LHS && getSelectedItemsInCheckList(LHS)) !== (RHS && parseInt(RHS, 10))
    );
  },
  COUNT_GREATER_THAN(LHS, RHS, data) {
    return (
      (LHS && getSelectedItemsInCheckList(LHS)) > (RHS && parseInt(RHS, 10))
    );
  },
  COUNT_LESS_THAN(LHS, RHS, data) {
    return (
      (LHS && getSelectedItemsInCheckList(LHS)) < (RHS && parseInt(RHS, 10))
    );
  },
  COUNT_GREATER_THAN_OR_EQUAL_TO(LHS, RHS, data) {
    return (
      (LHS && getSelectedItemsInCheckList(LHS)) >= (RHS && parseInt(RHS, 10))
    );
  },
  COUNT_LESS_THAN_OR_EQUAL_TO(LHS, RHS, data) {
    return (
      (LHS && getSelectedItemsInCheckList(LHS)) <= (RHS && parseInt(RHS, 10))
    );
  },
  EMPTY(LHS, RHS, data) {
    return !LHS;
  },
  NOT_EMPTY(LHS, RHS, data) {
    return !!LHS;
  }
};

const Reference = {
  EQUAL_TO(LHS, RHS, data) {
    return isEqual(LHS, RHS);
  },
  NOT_EQUAL_TO(LHS, RHS, data) {
    return !isEqual(LHS, RHS);
  }
};

const Currency = {
  EQUAL_TO(LHS, RHS, data) {
    return LHS === RHS;
  },
  NOT_EQUAL_TO(LHS, RHS, data) {
    return LHS ? LHS !== RHS : true;
  },
  GREATER_THAN(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    let rhsvalue = getCurrencyValue(RHS);
    return (
      isSameCurrencyUnit(LHS, RHS) &&
      NumberClass.GREATER_THAN(lhsvalue, rhsvalue, data)
    );
  },
  GREATER_THAN_OR_EQUAL_TO(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    let rhsvalue = getCurrencyValue(RHS);
    return (
      isSameCurrencyUnit(LHS, RHS) &&
      NumberClass.GREATER_THAN_OR_EQUAL_TO(lhsvalue, rhsvalue, data)
    );
  },
  LESS_THAN(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    let rhsvalue = getCurrencyValue(RHS);
    return (
      isSameCurrencyUnit(LHS, RHS) &&
      NumberClass.LESS_THAN(lhsvalue, rhsvalue, data)
    );
  },
  LESS_THAN_OR_EQUAL_TO(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    let rhsvalue = getCurrencyValue(RHS);
    return (
      isSameCurrencyUnit(LHS, RHS) &&
      NumberClass.LESS_THAN_OR_EQUAL_TO(lhsvalue, rhsvalue, data)
    );
  },
  EMPTY(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    let rhsvalue = getCurrencyValue(RHS);
    return NumberClass.EMPTY(lhsvalue, rhsvalue, data);
  },
  NOT_EMPTY(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    let rhsvalue = getCurrencyValue(RHS);
    return NumberClass.NOT_EMPTY(lhsvalue, rhsvalue, data);
  }
};

const CurrencyValue = {
  EQUAL_TO(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    return NumberClass.EQUAL_TO(lhsvalue, RHS, data);
  },
  NOT_EQUAL_TO(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    return NumberClass.NOT_EQUAL_TO(lhsvalue, RHS, data);
  },
  GREATER_THAN(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    return NumberClass.GREATER_THAN(lhsvalue, RHS, data);
  },
  GREATER_THAN_OR_EQUAL_TO(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    return NumberClass.GREATER_THAN_OR_EQUAL_TO(lhsvalue, RHS, data);
  },
  LESS_THAN(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    return NumberClass.LESS_THAN(lhsvalue, RHS, data);
  },
  LESS_THAN_OR_EQUAL_TO(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    return NumberClass.LESS_THAN_OR_EQUAL_TO(lhsvalue, RHS, data);
  },
  EMPTY(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    return NumberClass.EMPTY(lhsvalue, RHS, data);
  },
  NOT_EMPTY(LHS, RHS, data) {
    let lhsvalue = getCurrencyValue(LHS);
    return NumberClass.NOT_EMPTY(lhsvalue, RHS, data);
  }
};

const CurrencyUnit = {
  EQUAL_TO(LHS, RHS, data) {
    return LHS?.split(" ")[1] === RHS;
  },
  NOT_EQUAL_TO(LHS, RHS, data) {
    return LHS ? LHS.split(" ")[1] !== RHS : true;
  }
};

export const DateClass = {
  EQUAL_TO(LHS, RHS, data) {
    if (VALID_DATE_RELATIVE_VALUES.has(RHS)) {
      return resolveOrExecuteRelativeValue({
        LHS,
        RHS,
        fieldType: "Date",
        operator: "EQUAL_TO"
      });
    }
    return (
      (LHS && new Date(LHS).getTime()) === (RHS && new Date(RHS).getTime())
    );
  },
  NOT_EQUAL_TO(LHS, RHS, data) {
    if (VALID_DATE_RELATIVE_VALUES.has(RHS)) {
      return resolveOrExecuteRelativeValue({
        LHS,
        RHS,
        fieldType: "Date",
        operator: "NOT_EQUAL_TO"
      });
    }
    if (!LHS && !RHS) return false;
    if (!LHS || !RHS) return true;
    return LHS
      ? new Date(LHS).getTime() !== (RHS && new Date(RHS).getTime())
      : true;
  },
  GREATER_THAN(LHS, RHS, data) {
    if (VALID_DATE_RELATIVE_VALUES.has(RHS)) {
      return resolveOrExecuteRelativeValue({
        LHS,
        RHS,
        fieldType: "Date",
        operator: "GREATER_THAN"
      });
    }
    return (LHS && new Date(LHS).getTime()) > (RHS && new Date(RHS).getTime());
  },
  GREATER_THAN_OR_EQUAL_TO(LHS, RHS, data) {
    if (VALID_DATE_RELATIVE_VALUES.has(RHS)) {
      return resolveOrExecuteRelativeValue({
        LHS,
        RHS,
        fieldType: "Date",
        operator: "GREATER_THAN_OR_EQUAL_TO"
      });
    }
    return (LHS && new Date(LHS).getTime()) >= (RHS && new Date(RHS).getTime());
  },
  LESS_THAN(LHS, RHS, data) {
    if (VALID_DATE_RELATIVE_VALUES.has(RHS)) {
      return resolveOrExecuteRelativeValue({
        LHS,
        RHS,
        fieldType: "Date",
        operator: "LESS_THAN"
      });
    }
    return (LHS && new Date(LHS).getTime()) < (RHS && new Date(RHS).getTime());
  },
  LESS_THAN_OR_EQUAL_TO(LHS, RHS, data) {
    if (VALID_DATE_RELATIVE_VALUES.has(RHS)) {
      return resolveOrExecuteRelativeValue({
        LHS,
        RHS,
        fieldType: "Date",
        operator: "LESS_THAN_OR_EQUAL_TO"
      });
    }
    return (LHS && new Date(LHS).getTime()) <= (RHS && new Date(RHS).getTime());
  },
  BETWEEN(LHS, RHS, date) {
    let [from, to] = RHS.map((date) => new Date(date).getTime());
    let actualdate = LHS && new Date(LHS).getTime();
    return actualdate && actualdate <= to && actualdate >= from;
  },
  EMPTY(LHS, RHS, data) {
    return !LHS;
  },
  NOT_EMPTY(LHS, RHS, data) {
    return !!LHS;
  }
};

export const DateTimeClass = {
  EQUAL_TO(LHS, RHS, data) {
    if (VALID_DATE_RELATIVE_VALUES.has(RHS)) {
      return resolveOrExecuteRelativeValue({
        LHS,
        RHS,
        fieldType: "DateTime",
        operator: "EQUAL_TO"
      });
    }
    let lhsdate = LHS && new Date(formatDateTime(LHS)).getTime();
    let rhsdate = RHS && new Date(formatDateTime(RHS)).getTime();
    return lhsdate && lhsdate === rhsdate;
  },
  NOT_EQUAL_TO(LHS, RHS, data) {
    if (VALID_DATE_RELATIVE_VALUES.has(RHS)) {
      return resolveOrExecuteRelativeValue({
        LHS,
        RHS,
        fieldType: "DateTime",
        operator: "NOT_EQUAL_TO"
      });
    }
    let lhsdate = LHS && new Date(formatDateTime(LHS)).getTime();
    let rhsdate = RHS && new Date(formatDateTime(RHS)).getTime();
    return lhsdate ? lhsdate !== rhsdate : true;
  },
  GREATER_THAN(LHS, RHS, data) {
    if (VALID_DATE_RELATIVE_VALUES.has(RHS)) {
      return resolveOrExecuteRelativeValue({
        LHS,
        RHS,
        fieldType: "DateTime",
        operator: "GREATER_THAN"
      });
    }
    let lhsdate = LHS && new Date(formatDateTime(LHS)).getTime();
    let rhsdate = RHS && new Date(formatDateTime(RHS)).getTime();
    return lhsdate && lhsdate > rhsdate;
  },
  GREATER_THAN_OR_EQUAL_TO(LHS, RHS, data) {
    if (VALID_DATE_RELATIVE_VALUES.has(RHS)) {
      return resolveOrExecuteRelativeValue({
        LHS,
        RHS,
        fieldType: "DateTime",
        operator: "GREATER_THAN_OR_EQUAL_TO"
      });
    }
    let lhsdate = LHS && new Date(formatDateTime(LHS)).getTime();
    let rhsdate = RHS && new Date(formatDateTime(RHS)).getTime();
    return lhsdate && lhsdate >= rhsdate;
  },
  LESS_THAN(LHS, RHS, data) {
    if (VALID_DATE_RELATIVE_VALUES.has(RHS)) {
      return resolveOrExecuteRelativeValue({
        LHS,
        RHS,
        fieldType: "DateTime",
        operator: "LESS_THAN"
      });
    }
    let lhsdate = LHS && new Date(formatDateTime(LHS)).getTime();
    let rhsdate = RHS && new Date(formatDateTime(RHS)).getTime();
    return lhsdate && lhsdate < rhsdate;
  },
  LESS_THAN_OR_EQUAL_TO(LHS, RHS, data) {
    if (VALID_DATE_RELATIVE_VALUES.has(RHS)) {
      return resolveOrExecuteRelativeValue({
        LHS,
        RHS,
        fieldType: "DateTime",
        operator: "LESS_THAN_OR_EQUAL_TO"
      });
    }
    let lhsdate = LHS && new Date(formatDateTime(LHS)).getTime();
    let rhsdate = RHS && new Date(formatDateTime(RHS)).getTime();
    return lhsdate && lhsdate <= rhsdate;
  },
  BETWEEN(LHS, RHS, date) {
    let [from, to] = RHS.map((date) =>
      new Date(formatDateTime(date)).getTime()
    );
    let actualdate = LHS && new Date(formatDateTime(LHS)).getTime();
    return actualdate && actualdate <= to && actualdate >= from;
  },
  EMPTY(LHS, RHS, data) {
    return !LHS;
  },
  NOT_EMPTY(LHS, RHS, data) {
    return !!LHS;
  }
};

const Email = {
  EQUAL_TO(LHS, RHS, data) {
    return LHS === RHS;
  },
  NOT_EQUAL_TO(LHS, RHS, data) {
    return LHS !== RHS;
  },
  CONTAINS(LHS, RHS, data) {
    return LHS?.indexOf(RHS) > -1;
  },
  NOT_CONTAINS(LHS, RHS, data) {
    return LHS?.indexOf(RHS) === -1;
  },
  STARTS_WITH(LHS, RHS, data) {
    return LHS?.indexOf(RHS) === 0;
  },
  ENDS_WITH(LHS, RHS, data) {
    return LHS && LHS.indexOf(RHS) + RHS?.length === LHS.length;
  },
  DOMAIN_EQUAL_TO(LHS, RHS, data) {
    return LHS && LHS.match("@([^.]+).")?.[1] === RHS;
  },
  DOMAIN_NOT_EQUAL_TO(LHS, RHS, data) {
    return LHS ? LHS.match("@([^.]+).")?.[1] !== RHS : true;
  }
};

export const FieldClass = {
  Text: Text,
  Boolean: BooleanClass,
  Number: NumberClass,
  Attachment: Attachment,
  Slider: NumberClass,
  StarRating: NumberClass,
  User: User,
  Select: Select,
  Multiselect: Multiselect,
  MultiUser: MultiUser,
  Checklist: Checklist,
  Reference: Reference,
  Currency: Currency,
  CurrencyValue: CurrencyValue,
  CurrencyUnit: CurrencyUnit,
  Date: DateClass,
  DateTime: DateTimeClass,
  Email: Email
};
